import { useCallback, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { cancelVirtualAppointment } from '../../services/virtualAppointments';
import {
    PATIENT_VIRTUAL_APPOINTMENTS_KEY,
    VIRTUAL_APPOINTMENTS_OVERVIEW_KEY
} from '../../hooks/queryKeys';
import { apiErrorMessage } from '../../utils/apiError';

const CANCEL_FALLBACK = "Couldn't cancel this VA. Try again.";

export interface CancelVirtualAppointmentAction {
    pending: boolean;
    error: string | null;
    /** `reason` must already be trimmed and valid; see validateCancelReason. */
    cancel: (reason: string, onSuccess: () => void) => void;
    reset: () => void;
}

// Success stays pending until both the overview and the patient's VAs refetch, so no view keeps
// offering Cancel on a canceled VA; a failure refetches too since a 409 means the VA moved on.
export function useCancelVirtualAppointment(
    patientId: string,
    vaId: string
): CancelVirtualAppointmentAction {
    const queryClient = useQueryClient();
    const refresh = useCallback(
        () =>
            Promise.all([
                queryClient.invalidateQueries({ queryKey: [VIRTUAL_APPOINTMENTS_OVERVIEW_KEY] }),
                queryClient.invalidateQueries({
                    queryKey: PATIENT_VIRTUAL_APPOINTMENTS_KEY(patientId)
                })
            ]),
        [queryClient, patientId]
    );
    const { mutate, reset, isPending, isError, error } = useMutation({
        mutationFn: (reason: string) => cancelVirtualAppointment(patientId, vaId, reason),
        onSuccess: refresh,
        onError: () => void refresh()
    });
    const cancel = useCallback(
        (reason: string, onSuccess: () => void) => mutate(reason, { onSuccess }),
        [mutate]
    );
    const message = isError ? apiErrorMessage(error, CANCEL_FALLBACK) : null;
    return useMemo(
        () => ({ pending: isPending, error: message, cancel, reset }),
        [isPending, message, cancel, reset]
    );
}
